import React, { PropsWithChildren, useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from '@/store/useAuth';
import { useGetProfile } from '@/hooks/use-get-profile';
import { Spiner } from '@/ui-kit';
import { ErrorMessage } from '../error-message';
import { Outlet, useLocation } from 'react-router-dom';
import { IntroBanned } from '@/components/intro/intro-banned';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useTranslation } from 'react-i18next';
import { SignInForm } from '../sign-in-form';
import { jwtService } from '@/services/jwt.service';
import WebApp from '@twa-dev/sdk';
import { ApiClient } from '@/services/api.service';
import { AxiosError } from 'axios';
import { routesApp } from '@/constans/routes';
import { loadLangTranslations } from '@/utils/translations';

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { data, isLoading, isError, refetch, } = useGetProfile();
    const { setProfile, setProfileWasRemovedError } = useAuth((state) => state);
    const [ removedProfileWasRefetched, setRemovedProfileWasRefetched ] = useState(false);
    const [isBanned, setIsBanned] = useState(false);
    const [metrika, setMetrika] = useState(null);
    const [isAuth, setIsAuth] = useState<boolean | null>(WebApp.initData.length ? true : null);
    
    const { t, i18n } = useTranslation();

    const location = useLocation()

    useLayoutEffect(() => {
        if (data) {
            const isProfileRemoved = data.status === 410
            if (!isProfileRemoved)
                setProfileWasRemovedError(null)
            if (
                isProfileRemoved &&
                !removedProfileWasRefetched
            ) {
                setProfileWasRemovedError(true)
                refetch()
                setRemovedProfileWasRefetched(true)
            } else if (!data.data?.is_banned) {
                if (!!data?.data && data.data?.metrics_key)
                    setMetrika(data.data.metrics_key);
                const profile = data?.data
                setProfile(profile);
                loadLangTranslations(profile.lang)
            } else setIsBanned(true);
        }
    }, [data, setProfile, removedProfileWasRefetched]);

    useEffect(() => {
        if (metrika) callYaMetrika(metrika);
    }, [metrika]);

    useEffect(() => {
        ApiClient.interceptors.response.use(
            res => res,
            async err => {
                if (!(err instanceof AxiosError)) return Promise.reject(err);
                if (err.response?.status !== 401) return Promise.reject(err);
                if (jwtService.getRefreshTokenWasUsedState()) setIsAuth(false)
                jwtService.setRefreshTokenWasUsedState(false)
                return Promise.reject(err)
            }
        );
    }, [])

    useEffect(() => {
        if (data) {
            const status = data.status
            if (status === 401) setIsAuth(false)
            else if (status >= 200 && status < 300) setIsAuth(true)
        }
    }, [data])

    if (isBanned) {
        return <IntroBanned />;
    }

    if (
        isAuth === false &&
        location.pathname !== routesApp.index
    ) return <SignInForm onLogin={refetch} />;
    
    if (isError) {
        return <ErrorMessage message={t('error_loading_profile')} />;
    }

    return isLoading ? <Spiner /> : <>{children || <Outlet />}</>;
};
